import { useRef } from "react";
import {
  Bot, LayoutDashboard, Settings, Code2, MessageSquare, Shield, LogOut, Plug,
  Building2, Users, Crown, BarChart3, Webhook, Radio, FileSearch, Wrench,
  Headphones, Inbox, SlidersHorizontal, Sparkles, Phone, PhoneCall,
  PhoneMissed, Voicemail, ListChecks, BarChart, Ticket, Cpu, ShieldCheck, ClipboardList,
  Brain,
} from "lucide-react";
import { useLocation, Link } from "wouter";
import { useAuth, useLogout, isLiveChatRole, isLiveChatAdminRole, hasPermission } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { Badge } from "@/components/ui/badge";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";

const mainNavItems = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard, feature: "dashboard" },
  // module: "bots" — hidden when SuperAdmin disables the BOTS module for tenants.
  { title: "Chatbots", url: "/chatbots", icon: Bot, perm: "chatbots.view", feature: "chatbots", module: "bots" },
  // Entitlement-gated: shown when the org's plan includes the
  // `conversations` feature AND the user holds `conversations.view`
  // (super_admin bypasses both inside hasFeature/hasPermission).
  { title: "Conversations", url: "/conversations", icon: MessageSquare, perm: "conversations.view", feature: "conversations" },
];

const toolsNavItems = [
  // module: "bots" — hidden when SuperAdmin disables the BOTS module for tenants.
  { title: "Setup Agent", url: "/setup-agent", icon: Sparkles, perm: "chatbots.manage", feature: "setup_agent", module: "bots" },
  // Compliance & Settings are org-admin self-service pages that hit
  // role-gated /api/organization/* endpoints; there is no granular
  // permission behind them, so they stay on the org-admin role lane.
  { title: "Compliance Center", url: "/compliance", icon: ShieldCheck, adminOnly: true, feature: "compliance" },
  { title: "API Docs", url: "/api-docs", icon: Code2, perm: "api_docs.manage", feature: "apidocs" },
  // Org-level hub; per-bot configuration lives at /chatbots/:id/integrations.
  { title: "Integrations", url: "/integrations", icon: Plug, perm: "integrations.manage", feature: "integrations" },
  { title: "Settings", url: "/settings", icon: Settings, adminOnly: true, feature: "settings" },
];

// NLP nav — keyword intent classification and entity extraction management.
// Shown to users who can manage bots (same gate as the NLP API controller).
const nlpNavItems = [
  { title: "NLP Intents", url: "/nlp/intents", icon: Brain, perm: "chatbots.manage" },
  { title: "NLP Entities", url: "/nlp/entities", icon: Brain, perm: "chatbots.manage" },
];

// Content Governance nav — knowledge publication lifecycle.
// Shown to users who hold any of the three governance permissions.
const governanceNavItems = [
  { title: "Content Governance", url: "/content-governance", icon: Brain, perm: "knowledge.manage" },
];

export function AppSidebar() {
  const [location] = useLocation();
  const { user, organization } = useAuth();
  const logoutMutation = useLogout();
  const branding = useBranding();
  // Once logout starts it must not be re-triggered. window.location.replace()
  // schedules navigation synchronously but the async mutationFn still resolves
  // afterwards, briefly re-enabling the button. A ref (not state) lets us
  // suppress clicks for the entire lifetime of this component without causing
  // an extra render.
  const logoutStartedRef = useRef(false);

  const isSuperAdmin = user?.role === "super_admin";
  const adminRoles = ["admin", "org_admin", "it_admin", "hr_admin", "bot_admin"];
  const isClientAdmin = adminRoles.includes(user?.role || "");
  const isLiveAgentRole = user?.role === "live_agent" || user?.role === "supervisor" || user?.role === "auditor";
  const features: string[] = ((organization as any)?.features) || [];
  const hasFeature = (k: string) => isSuperAdmin || features.includes(k);
  // Globally disabled module categories from /api/auth/me (empty for SA).
  // Used to hide module-specific sidebar items when SA disables a module.
  const disabledModules: string[] = ((organization as any)?.disabledModules) || [];
  const isModuleOn = (m?: string) => !m || isSuperAdmin || !disabledModules.includes(m);
  // Single source of truth for nav visibility: the permissions the
  // master-admin granted this user's role (super_admin bypasses inside
  // hasPermission). Plan features remain a hard ceiling for Calls/Tickets.
  const can = (perm: string) => hasPermission(user, perm);
  const showCalls = hasFeature("calls") && (can("calls.view") || can("calls.operate") || can("calls.supervise") || can("calls.admin") || can("calls.reports.view"));
  const showSupervisor = hasFeature("calls.supervisor") && can("calls.supervise");
  const showReports = hasFeature("calls.reports") && can("calls.reports.view");
  const showCallSettings = hasFeature("calls.settings") && can("calls.admin");
  const showTickets = hasFeature("tickets") && can("tickets.view");
  // "Manage Org" is a role-gated self-service page (no backing permission),
  // so org admins always get the group; other roles get it when they hold
  // any of the permission-backed entries.
  const showOrgGroup = isClientAdmin || can("conversations.view") || can("groups.manage") || can("users.manage") || can("roles.manage") || can("sso.manage") || can("tools.view");
  const isInternalMode = organization?.mode === "internal";
  const isLiveOnly = isLiveChatRole(user?.role);
  // Live-chat staff and end-user staff both get a "look but don't
  // touch" treatment for everything outside their lane:
  //   - end-user staff → only the chat portal at "/" is interactive
  //   - live-chat staff → only the Live Chat group is interactive
  //   - everything else renders disabled so the role's audience can
  //     see the surface area without being able to act on it.
  const isStaff = !isSuperAdmin && !isClientAdmin && !isLiveOnly;
  // Live Chat is enabled in the sidebar for: live_* roles AND org/super
  // admins (admins implicitly supervise the queue too).
  const showLiveChat = hasFeature("livechat") && (can("livechat.operate") || can("livechat.supervise") || can("livechat.admin"));
  // Interactivity is permission-driven, not role-lane driven: anyone who
  // can see the group holds a livechat.* permission, so the entries are
  // live (this keeps Supervisors, who aren't in the isLiveOnly role set,
  // from getting a disabled Live Chat nav).
  const liveChatDisabled = !showLiveChat;

  // URLs that have more-specific child entries elsewhere in the sidebar
  // (e.g. "/settings" has "/settings/integrations" as a sibling row).
  // For these, a prefix match would light up BOTH the parent and the
  // child at the same time. Match exact-only so only the most-specific
  // entry highlights. "/" is exact for the same reason — every route
  // starts with "/".
  const EXACT_MATCH_PARENT_URLS = new Set(["/", "/admin", "/settings"]);
  function isActive(url: string) {
    if (EXACT_MATCH_PARENT_URLS.has(url)) return location === url;
    return location === url || location.startsWith(url + "/");
  }

  return (
    <Sidebar data-focus-region="sidebar">
      <SidebarHeader className="p-5 pb-4">
        <Link href="/">
          <div className="flex items-center gap-2.5 cursor-pointer" data-testid="link-home">
            {(organization as any)?.logo ? (
              <img
                src={(organization as any).logo}
                alt={organization?.name || branding.productName}
                className="h-10 w-auto max-w-[180px] object-contain"
                data-testid="img-brand-logo"
              />
            ) : (
              <div className="flex flex-col">
                {!organization?.name && (
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] font-medium tracking-[0.15em] uppercase text-sidebar-foreground/50">App</span>
                    <span className="text-[11px] font-bold tracking-[0.15em] uppercase text-[hsl(199,89%,48%)]">Maisters</span>
                  </div>
                )}
                <h1 className="text-base font-bold tracking-tight text-sidebar-foreground leading-tight" data-testid="text-brand-name">{organization?.name || branding.productName}</h1>
                <p className="text-[10px] text-sidebar-foreground/40 mt-0.5">{(organization as any)?.tagline || branding.tagline}</p>
              </div>
            )}
          </div>
        </Link>
        {organization && (
          <div className="mt-3 p-2 rounded-md bg-sidebar-accent/50">
            <p className="text-[11px] font-medium text-sidebar-foreground truncate" data-testid="text-org-name">{organization.name}</p>
            <div className="flex items-center gap-1 mt-1">
              {isSuperAdmin ? (
                <Badge variant="default" className="text-[9px] bg-amber-600 hover:bg-amber-700">
                  <Crown className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" />Super Admin
                </Badge>
              ) : isClientAdmin ? (
                <Badge variant="default" className="text-[9px]">
                  <Shield className="w-2.5 h-2.5 mr-0.5" aria-hidden="true" />Admin
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] border-sidebar-border text-sidebar-foreground/60">{organization.plan}</Badge>
              )}
            </div>
          </div>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">
            Main
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems
                .filter((item) => isModuleOn((item as any).module)
                  && (!(item as any).feature || hasFeature((item as any).feature))
                  && (!(item as any).perm || can((item as any).perm)))
                .map((item) => {
                // For staff: only the Dashboard (which renders the
                // StaffPortal at "/") is interactive. The rest are
                // visible but disabled so staff users can see the
                // platform's surface area without being able to act
                // on it.
                // For live-chat-only roles: every Main item is
                // disabled — they only get the Live Chat group below.
                // The "/" route is overridden in App.tsx to render the
                // operator dashboard for them, so Dashboard still
                // works as their landing.
                const disabled = (isStaff && item.url !== "/") || (isLiveOnly && item.url !== "/");
                const testId = `link-nav-${item.title.toLowerCase().replace(/\s/g, "-")}`;
                return (
                  <SidebarMenuItem key={item.title}>
                    {disabled ? (
                      <SidebarMenuButton
                        aria-disabled="true"
                        title="Available to administrators"
                        className="opacity-40 cursor-not-allowed pointer-events-none"
                        data-testid={testId}
                      >
                        <item.icon className="w-4 h-4" aria-hidden="true" />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton asChild data-active={isActive(item.url)}>
                        <Link href={item.url} data-testid={testId}>
                          <item.icon className="w-4 h-4" aria-hidden="true" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">
            Tools
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {toolsNavItems
                // Permission-backed entries follow the master-admin's role
                // setup (super_admin bypasses via hasPermission). Entries
                // with no backing permission (adminOnly) stay on the
                // org-admin role lane.
                // A Tools entry is visible only when BOTH hold: the plan
                // includes the feature AND the user's role can access it.
                // Items tagged with `module` are also hidden when that
                // module is globally disabled by SuperAdmin.
                .filter((item) => isModuleOn((item as any).module)
                  && (!(item as any).feature || hasFeature((item as any).feature))
                  && ((item as any).perm ? can((item as any).perm) : (isClientAdmin || isSuperAdmin)))
                .map((item) => {
                  const testId = `link-nav-${item.title.toLowerCase().replace(/\s/g, "-")}`;
                  // Staff and live-chat-only roles see Tools entries
                  // but cannot navigate to them.
                  if (isStaff || isLiveOnly) {
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          aria-disabled="true"
                          title="Available to administrators"
                          className="opacity-40 cursor-not-allowed pointer-events-none"
                          data-testid={testId}
                        >
                          <item.icon className="w-4 h-4" aria-hidden="true" />
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }
                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild data-active={isActive(item.url)}>
                        <Link href={item.url} data-testid={testId}>
                          <item.icon className="w-4 h-4" aria-hidden="true" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              {(user?.role === "super_admin" || user?.role === "org_admin") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/discovery-workspace")}>
                    <Link href="/discovery-workspace" data-testid="link-nav-discovery-workspace">
                      <ClipboardList className="w-4 h-4" aria-hidden="true" />
                      <span>Discovery &amp; Requirements</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Content Governance — knowledge publication lifecycle.
            Hidden when the bots module is globally disabled (content governance
            is chatbot-specific and has no purpose without the bots module). */}
        {isModuleOn("bots") && !isStaff && !isLiveOnly && (can("knowledge.manage") || can("content.review") || can("content.publish")) && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">
              Governance
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/content-governance")}
                    data-testid="link-nav-content-governance">
                    <Link href="/content-governance">
                      <Brain className="w-4 h-4" aria-hidden="true" />
                      <span>Content Governance</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* FAQ Management — org-level FAQ & category CRUD.
            Gated by permission only (not role category) so custom tenant
            roles like Content_Manager that hold faq.view are included. */}
        {can("faq.view") && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">
              FAQ
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/faq-management")}
                    data-testid="link-nav-faq-management">
                    <Link href="/faq-management">
                      <Brain className="w-4 h-4" aria-hidden="true" />
                      <span>FAQ Management</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* NLP intent / entity management — shown to bot managers only.
            Hidden when the bots module is globally disabled. */}
        {isModuleOn("bots") && !isStaff && !isLiveOnly && nlpNavItems.some(item => can(item.perm)) && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">
              NLP
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {nlpNavItems.filter(item => can(item.perm)).map(item => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild data-active={isActive(item.url)}
                      data-testid={`link-nav-${item.title.toLowerCase().replace(/\s/g, "-")}`}>
                      <Link href={item.url}>
                        <item.icon className="w-4 h-4" aria-hidden="true" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showCalls && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">Calls</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {hasFeature("calls.workspace") && can("calls.operate") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/calls")}>
                    <Link href="/calls" data-testid="link-nav-calls"><Phone className="w-4 h-4" aria-hidden="true" /><span>Agent Workspace</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("calls.logs") && can("calls.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/calls/logs")}>
                    <Link href="/calls/logs" data-testid="link-nav-call-logs"><ListChecks className="w-4 h-4" aria-hidden="true" /><span>Call Logs</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("calls.voicemail") && can("calls.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/calls/voicemail")}>
                    <Link href="/calls/voicemail" data-testid="link-nav-voicemail"><Voicemail className="w-4 h-4" aria-hidden="true" /><span>Voicemail</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("calls.callbacks") && can("calls.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/calls/callbacks")}>
                    <Link href="/calls/callbacks" data-testid="link-nav-callbacks"><PhoneMissed className="w-4 h-4" aria-hidden="true" /><span>Callbacks</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {showSupervisor && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/calls/supervisor")}>
                      <Link href="/calls/supervisor" data-testid="link-nav-supervisor"><Headphones className="w-4 h-4" aria-hidden="true" /><span>Supervisor</span></Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {showReports && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/calls/reports")}>
                      <Link href="/calls/reports" data-testid="link-nav-call-reports"><BarChart className="w-4 h-4" aria-hidden="true" /><span>Reports</span></Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {showCallSettings && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/calls/settings")}>
                      <Link href="/calls/settings" data-testid="link-nav-call-settings"><SlidersHorizontal className="w-4 h-4" aria-hidden="true" /><span>Call Settings</span></Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showTickets && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">Tickets</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/tickets")}>
                    <Link href="/tickets" data-testid="link-nav-tickets"><Ticket className="w-4 h-4" aria-hidden="true" /><span>Tickets</span></Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showLiveChat && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">Live Chat</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {hasFeature("livechat.operator") && (
                <SidebarMenuItem>
                  {liveChatDisabled ? (
                    <SidebarMenuButton aria-disabled="true" title="Available to live chat operators" className="opacity-40 cursor-not-allowed pointer-events-none" data-testid="link-nav-live-chat">
                      <Headphones className="w-4 h-4" aria-hidden="true" /><span>Operator Dashboard</span>
                    </SidebarMenuButton>
                  ) : (
                    <SidebarMenuButton asChild data-active={isActive("/live-chat")}>
                      <Link href="/live-chat" data-testid="link-nav-live-chat">
                        <Headphones className="w-4 h-4" aria-hidden="true" /><span>Operator Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
                )}
                {hasFeature("livechat.offline") && can("livechat.admin") && (
                <SidebarMenuItem>
                  {liveChatDisabled ? (
                    <SidebarMenuButton aria-disabled="true" title="Available to live chat operators" className="opacity-40 cursor-not-allowed pointer-events-none" data-testid="link-nav-live-chat-offline">
                      <Inbox className="w-4 h-4" aria-hidden="true" /><span>Offline Messages</span>
                    </SidebarMenuButton>
                  ) : (
                    <SidebarMenuButton asChild data-active={isActive("/live-chat/offline")}>
                      <Link href="/live-chat/offline" data-testid="link-nav-live-chat-offline">
                        <Inbox className="w-4 h-4" aria-hidden="true" /><span>Offline Messages</span>
                      </Link>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
                )}
                {hasFeature("livechat.settings") && can("livechat.admin") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/live-chat/settings")}>
                      <Link href="/live-chat/settings" data-testid="link-nav-live-chat-settings">
                        <SlidersHorizontal className="w-4 h-4" aria-hidden="true" /><span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">Administration</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin")}>
                    <Link href="/admin" data-testid="link-nav-admin">
                      <Crown className="w-4 h-4" aria-hidden="true" />
                      <span>Platform Admin</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/organizations")}>
                    <Link href="/admin/organizations" data-testid="link-nav-admin-orgs">
                      <Building2 className="w-4 h-4" aria-hidden="true" />
                      <span>Organizations</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {hasPermission(user, "users.manage") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/admin/users")}>
                      <Link href="/admin/users" data-testid="link-nav-admin-users">
                        <Users className="w-4 h-4" aria-hidden="true" />
                        <span>Users</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {hasPermission(user, "roles.manage") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/admin/org/roles")}>
                      <Link href="/admin/org/roles" data-testid="link-nav-admin-roles">
                        <Shield className="w-4 h-4" aria-hidden="true" />
                        <span>Roles</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/integrations")}>
                    <Link href="/admin/integrations" data-testid="link-nav-admin-integrations">
                      <Plug className="w-4 h-4" aria-hidden="true" />
                      <span>Integrations</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/logs")}>
                    <Link href="/logs" data-testid="link-nav-application-logs">
                      <FileSearch className="w-4 h-4" aria-hidden="true" />
                      <span>Application Logs</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {showOrgGroup && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/30 text-[10px] uppercase tracking-[0.12em] font-medium">Organization</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {hasFeature("organization.manage") && (isClientAdmin || isSuperAdmin) && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/org")}>
                    <Link href="/admin/org" data-testid="link-nav-org-admin">
                      <Shield className="w-4 h-4" aria-hidden="true" />
                      <span>Manage Org</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {/* Staff users (non-admin, non-live-only): personal history —
                    own conversations only, backed by /api/v1/my/history.
                    Only needs conversations.view; no plan feature required. */}
                {isStaff && can("conversations.view") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/my-history")}>
                      <Link href="/my-history" data-testid="link-nav-history">
                        <MessageSquare className="w-4 h-4" aria-hidden="true" />
                        <span>History</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {/* Admin users: org-wide conversation history.
                    Requires the organization.history plan feature. */}
                {!isStaff && hasFeature("organization.history") && can("conversations.view") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/history")}>
                      <Link href="/history" data-testid="link-nav-history">
                        <MessageSquare className="w-4 h-4" aria-hidden="true" />
                        <span>History</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {hasFeature("workforce") && can("groups.manage") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/settings/groups")}>
                    <Link href="/settings/groups" data-testid="link-nav-groups">
                      <Users className="w-4 h-4" aria-hidden="true" />
                      <span>Workforce</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("users.manage") && can("users.manage") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/admin/org/users")}>
                      <Link href="/admin/org/users" data-testid="link-nav-org-users">
                        <Users className="w-4 h-4" aria-hidden="true" />
                        <span>Users</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {hasFeature("roles.manage") && can("roles.manage") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/admin/org/roles")}>
                      <Link href="/admin/org/roles" data-testid="link-nav-roles">
                        <Shield className="w-4 h-4" aria-hidden="true" />
                        <span>Roles</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {hasFeature("sso") && can("sso.manage") && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild data-active={isActive("/settings/sso")}>
                      <Link href="/settings/sso" data-testid="link-nav-sso">
                        <Shield className="w-4 h-4" aria-hidden="true" />
                        <span>Identity & SSO</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {can("tools.view") && !isStaff && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/approvals")}>
                    <Link href="/approvals" data-testid="link-nav-approvals">
                      <ShieldCheck className="w-4 h-4" aria-hidden="true" />
                      <span>Approvals</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("audit") && can("audit.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/audit") && !isActive("/admin/audit/tools")}>
                    <Link href="/admin/audit" data-testid="link-nav-general-audit">
                      <ClipboardList className="w-4 h-4" aria-hidden="true" />
                      <span>Audit Log</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("audit") && can("tools.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/audit/tools")}>
                    <Link href="/admin/audit/tools" data-testid="link-nav-tool-audit">
                      <Wrench className="w-4 h-4" aria-hidden="true" />
                      <span>Tool Audit</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
                {hasFeature("audit") && can("audit.view") && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild data-active={isActive("/admin/security")}>
                    <Link href="/admin/security" data-testid="link-nav-security-events">
                      <Shield className="w-4 h-4" aria-hidden="true" />
                      <span>Security Events</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter className="p-5 pt-3">
        {user && (
          <div className="border-t border-sidebar-border pt-3">
            <div className="flex items-center justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate" data-testid="text-user-name">{user.name}</p>
                <p className="text-xs text-sidebar-foreground/80 truncate">{user.email}</p>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Link href="/profile">
                  <button
                    className="p-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
                    data-testid="button-user-settings"
                    aria-label="My settings"
                    title="My settings"
                  >
                    <Settings className="w-4 h-4" aria-hidden="true" />
                  </button>
                </Link>
                <button
                  onClick={() => {
                    if (logoutStartedRef.current) return;
                    logoutStartedRef.current = true;
                    logoutMutation.mutate();
                  }}
                  disabled={logoutMutation.isPending || logoutStartedRef.current}
                  className="p-1.5 rounded-md hover:bg-sidebar-accent text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="button-logout"
                  aria-label="Sign out"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        )}
        <div className="mt-3 px-2 py-1.5 rounded-md bg-sidebar-accent/40 border border-sidebar-border/40">
          <p className="text-[10px] font-semibold text-sidebar-foreground">Powered by {branding.poweredBy}</p>
          <p className="text-[9px] text-sidebar-foreground/90 mt-0.5">Houston, TX | appmaisters.com</p>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
